import React, { useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { tokenomicsConfig } from '@/data/tokenomicsData';
import { 
  Lock, Shield, Upload, Save, RefreshCw, 
  Download, Eye, EyeOff, ArrowLeft, AlertTriangle
} from 'lucide-react';

// Mock admin authentication
const MOCK_PIN = '1234';
const MOCK_IP_WHITELIST = ['127.0.0.1', 'localhost'];

const AdminPage = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [pin, setPin] = useState('');
  const [showPin, setShowPin] = useState(false);
  const [allocations, setAllocations] = useState(() => {
    // Initialize from localStorage or defaults
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem('neuro_tokenomics_allocations');
      if (stored) {
        return JSON.parse(stored);
      }
    }
    return tokenomicsConfig.allocations.map(a => ({
      ...a,
      percentage: a.percentage
    }));
  });
  const [hasChanges, setHasChanges] = useState(false);
  
  // Handle authentication
  const handleLogin = () => {
    if (pin === MOCK_PIN) {
      setIsAuthenticated(true);
      toast.success('Authentication successful');
    } else {
      toast.error('Invalid PIN');
    }
  };
  
  // Update allocation percentage
  const updateAllocation = (id, newPercentage) => {
    const numValue = parseFloat(newPercentage) || 0;
    setAllocations(prev => prev.map(a => 
      a.id === id ? { ...a, percentage: numValue } : a
    ));
    setHasChanges(true);
  };
  
  // Calculate total
  const totalPercentage = allocations.reduce((sum, a) => sum + a.percentage, 0);
  const isValid = Math.abs(totalPercentage - 100) < 0.01;
  
  // Save to localStorage
  const saveChanges = () => {
    if (!isValid) {
      toast.error('Total allocation must equal 100%');
      return;
    }
    
    localStorage.setItem('neuro_tokenomics_allocations', JSON.stringify(allocations));
    setHasChanges(false);
    toast.success('Changes saved successfully');
  };
  
  // Reset to defaults
  const resetToDefaults = () => {
    setAllocations(tokenomicsConfig.allocations.map(a => ({
      ...a,
      percentage: a.percentage
    })));
    localStorage.removeItem('neuro_tokenomics_allocations');
    setHasChanges(false);
    toast.info('Reset to default values');
  };
  
  // Export snapshot
  const exportSnapshot = useCallback(() => {
    const timestamp = Date.now();
    const data = {
      timestamp: new Date().toISOString(),
      allocations,
      totalPercentage
    };
    
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `tokenomics_snapshot_${timestamp}.json`;
    a.click();
    URL.revokeObjectURL(url);
    
    toast.success('Snapshot exported');
  }, [allocations, totalPercentage]);
  
  // Authentication Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass rounded-2xl p-8 max-w-md w-full"
        >
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-primary" />
            </div>
            <h1 className="font-heading text-2xl font-bold text-foreground">Admin Access</h1>
            <p className="text-sm text-muted-foreground mt-2">
              Enter your PIN to access the tokenomics admin panel
            </p>
          </div>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="pin">Admin PIN</Label>
              <div className="relative">
                <Input
                  id="pin"
                  type={showPin ? 'text' : 'password'}
                  value={pin}
                  onChange={(e) => setPin(e.target.value)}
                  placeholder="Enter PIN"
                  className="pr-10"
                  onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
                />
                <button
                  type="button"
                  onClick={() => setShowPin(!showPin)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showPin ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            
            <Button onClick={handleLogin} className="w-full">
              <Shield className="w-4 h-4 mr-2" />
              Authenticate
            </Button>
            
            <p className="text-xs text-center text-muted-foreground mt-4">
              Demo PIN: 1234
            </p>
          </div>
          
          <div className="mt-8 pt-6 border-t border-border">
            <a 
              href="/" 
              className="flex items-center justify-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Tokenomics Page
            </a>
          </div>
        </motion.div>
      </div>
    );
  }
  
  // Admin Dashboard
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="glass-dark border-b border-border sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <a href="/" className="flex items-center gap-3">
                <img 
                  src="https://customer-assets.emergentagent.com/job_neuromesh-token/artifacts/xe1d400s_NeuroMesh%20Logo%20%282%29.png" 
                  alt="NeuroMesh Logo" 
                  className="w-10 h-10 rounded-lg object-contain"
                />
                <div>
                  <span className="font-heading font-semibold text-foreground">NeuroMesh</span>
                  <span className="text-xs text-destructive ml-2 font-medium">ADMIN</span>
                </div>
              </a>
            </div>
            
            <div className="flex items-center gap-3">
              {hasChanges && (
                <span className="text-xs text-warning flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" />
                  Unsaved changes
                </span>
              )}
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setIsAuthenticated(false)}
              >
                <Lock className="w-4 h-4 mr-2" />
                Lock
              </Button>
            </div>
          </div>
        </div>
      </header>
      
      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs defaultValue="allocations" className="w-full">
          <TabsList className="glass mb-8">
            <TabsTrigger value="allocations">Allocations</TabsTrigger>
            <TabsTrigger value="vesting">Vesting</TabsTrigger>
            <TabsTrigger value="export">Export</TabsTrigger>
            <TabsTrigger value="settings">Settings</TabsTrigger>
          </TabsList>
          
          {/* Allocations Tab */}
          <TabsContent value="allocations">
            <div className="grid lg:grid-cols-3 gap-8">
              {/* Allocation Editor */}
              <div className="lg:col-span-2">
                <div className="glass rounded-xl p-6">
                  <h2 className="font-heading text-xl font-semibold text-foreground mb-6">
                    Edit Allocations
                  </h2>
                  
                  <div className="space-y-4">
                    {allocations.map((allocation) => (
                      <div 
                        key={allocation.id}
                        className="flex items-center gap-4 p-4 rounded-lg border border-border"
                      >
                        <div 
                          className="w-4 h-4 rounded-full flex-shrink-0"
                          style={{ backgroundColor: allocation.color }}
                        />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-foreground truncate">
                            {allocation.name}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {allocation.unlockedAtTGE ? 'Unlocked at TGE' : 'Locked at TGE'}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Input
                            type="number"
                            min="0"
                            max="100"
                            step="0.1"
                            value={allocation.percentage}
                            onChange={(e) => updateAllocation(allocation.id, e.target.value)}
                            className="w-24 text-right"
                          />
                          <span className="text-muted-foreground">%</span>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {/* Action Buttons */}
                  <div className="flex items-center gap-4 mt-6 pt-6 border-t border-border">
                    <Button 
                      onClick={saveChanges}
                      disabled={!hasChanges || !isValid}
                      className="flex-1"
                    >
                      <Save className="w-4 h-4 mr-2" />
                      Save Changes
                    </Button>
                    <Button 
                      variant="outline"
                      onClick={resetToDefaults}
                    >
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Reset
                    </Button>
                  </div>
                </div>
              </div>
              
              {/* Summary Panel */}
              <div className="space-y-6">
                <div className="glass rounded-xl p-6">
                  <h3 className="font-heading font-semibold text-foreground mb-4">Summary</h3>
                  
                  <div className={`
                    p-4 rounded-lg text-center mb-4
                    ${isValid ? 'bg-success/10' : 'bg-destructive/10'}
                  `}>
                    <p className="text-sm text-muted-foreground mb-1">Total Allocation</p>
                    <p className={`text-3xl font-bold ${isValid ? 'text-success' : 'text-destructive'}`}>
                      {totalPercentage.toFixed(1)}%
                    </p>
                    {!isValid && (
                      <p className="text-xs text-destructive mt-2">
                        Must equal 100%
                      </p>
                    )}
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Unlocked at TGE</span>
                      <span className="font-medium text-foreground">
                        {allocations.filter(a => a.unlockedAtTGE).reduce((s, a) => s + a.percentage, 0).toFixed(1)}%
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Locked at TGE</span>
                      <span className="font-medium text-foreground">
                        {allocations.filter(a => !a.unlockedAtTGE).reduce((s, a) => s + a.percentage, 0).toFixed(1)}%
                      </span>
                    </div>
                  </div>
                </div>
                
                <div className="glass rounded-xl p-6">
                  <h3 className="font-heading font-semibold text-foreground mb-4">Quick Actions</h3>
                  
                  <div className="space-y-3">
                    <Button 
                      variant="outline" 
                      className="w-full justify-start"
                      onClick={exportSnapshot}
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Export Snapshot
                    </Button>
                    <Button 
                      variant="outline" 
                      className="w-full justify-start"
                      onClick={() => toast.info('CSV upload coming soon')}
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      Upload CSV
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>
          
          {/* Vesting Tab */}
          <TabsContent value="vesting">
            <div className="glass rounded-xl p-6">
              <h2 className="font-heading text-xl font-semibold text-foreground mb-6">
                Vesting Schedule Configuration
              </h2>
              
              <div className="grid md:grid-cols-2 gap-6">
                {allocations.filter(a => a.vesting).map((allocation) => (
                  <div key={allocation.id} className="border border-border rounded-lg p-4">
                    <div className="flex items-center gap-3 mb-4">
                      <div 
                        className="w-4 h-4 rounded-full"
                        style={{ backgroundColor: allocation.color }}
                      />
                      <h4 className="font-medium text-foreground">{allocation.name}</h4>
                    </div>
                    
                    <div className="space-y-4">
                      <div>
                        <Label>Cliff (months)</Label>
                        <Input 
                          type="number" 
                          value={allocation.vesting?.cliffMonths || 0}
                          className="mt-1"
                          disabled
                        />
                      </div>
                      <div>
                        <Label>Vesting Duration (months)</Label>
                        <Input 
                          type="number" 
                          value={allocation.vesting?.vestingMonths || 0}
                          className="mt-1"
                          disabled
                        />
                      </div>
                    </div>
                    
                    <p className="text-xs text-muted-foreground mt-4">
                      Vesting schedule editing coming soon
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>
          
          {/* Export Tab */}
          <TabsContent value="export">
            <div className="glass rounded-xl p-6">
              <h2 className="font-heading text-xl font-semibold text-foreground mb-6">
                Export & Versioning
              </h2>
              
              <div className="grid md:grid-cols-3 gap-6">
                <button
                  onClick={exportSnapshot}
                  className="border border-border rounded-xl p-6 hover:border-primary/30 hover:bg-primary/5 transition-all text-left"
                >
                  <Download className="w-8 h-8 text-primary mb-4" />
                  <h4 className="font-medium text-foreground mb-2">Export Current Snapshot</h4>
                  <p className="text-sm text-muted-foreground">
                    Download the current configuration as JSON
                  </p>
                </button>
                
                <button
                  onClick={() => toast.info('Version history coming soon')}
                  className="border border-border rounded-xl p-6 hover:border-primary/30 hover:bg-primary/5 transition-all text-left opacity-60"
                >
                  <RefreshCw className="w-8 h-8 text-muted-foreground mb-4" />
                  <h4 className="font-medium text-foreground mb-2">Version History</h4>
                  <p className="text-sm text-muted-foreground">
                    View and restore previous versions
                  </p>
                </button>
                
                <button
                  onClick={() => toast.info('Publish feature coming soon')}
                  className="border border-border rounded-xl p-6 hover:border-primary/30 hover:bg-primary/5 transition-all text-left opacity-60"
                >
                  <Upload className="w-8 h-8 text-muted-foreground mb-4" />
                  <h4 className="font-medium text-foreground mb-2">Publish Version</h4>
                  <p className="text-sm text-muted-foreground">
                    Publish new tokenomics version
                  </p>
                </button>
              </div>
            </div>
          </TabsContent>
          
          {/* Settings Tab */}
          <TabsContent value="settings">
            <div className="glass rounded-xl p-6">
              <h2 className="font-heading text-xl font-semibold text-foreground mb-6">
                Admin Settings
              </h2>
              
              <div className="space-y-6">
                <div className="flex items-center justify-between p-4 border border-border rounded-lg">
                  <div>
                    <h4 className="font-medium text-foreground">DAO Integration</h4>
                    <p className="text-sm text-muted-foreground">Enable DAO governance controls</p>
                  </div>
                  <Switch disabled />
                </div>
                
                <div className="flex items-center justify-between p-4 border border-border rounded-lg">
                  <div>
                    <h4 className="font-medium text-foreground">Require Hardware Key</h4>
                    <p className="text-sm text-muted-foreground">Enforce hardware key authentication</p>
                  </div>
                  <Switch disabled />
                </div>
                
                <div className="flex items-center justify-between p-4 border border-border rounded-lg">
                  <div>
                    <h4 className="font-medium text-foreground">IP Whitelist</h4>
                    <p className="text-sm text-muted-foreground">Restrict access by IP address</p>
                  </div>
                  <Switch disabled />
                </div>
              </div>
              
              <p className="text-xs text-muted-foreground text-center mt-6">
                Advanced security settings require backend integration
              </p>
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default AdminPage;
